import axios from "axios";
import { showErrorToast } from "./toastService";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";
const REQUEST_TIMEOUT = Number(import.meta.env.VITE_HTTP_TIMEOUT_MS || 30000);
const RETRYABLE_STATUS_CODES = new Set([408, 425, 429, 500, 502, 503, 504]);

const pendingRequests = new Map();

const getRequestKey = (config = {}) => {
  const { method = "GET", url = "", params, data } = config;
  const serializedParams = params ? JSON.stringify(params) : "";
  const serializedData =
    typeof data === "string" ? data : data ? JSON.stringify(data) : "";
  return [method.toUpperCase(), url, serializedParams, serializedData].join(
    "|",
  );
};

const removePendingRequest = (config = {}) => {
  const key = getRequestKey(config);
  if (pendingRequests.has(key)) {
    const controller = pendingRequests.get(key);
    controller.abort();
    pendingRequests.delete(key);
  }
};

const getStoredValue = (key) => {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const setStoredValue = (key, value) => {
  if (typeof window === "undefined") return;
  try {
    if (value) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    // no-op
  }
};

export const normalizeError = (error) => {
  if (axios.isCancel(error)) {
    return {
      message: "Request cancelled",
      status: "CANCELLED",
      isCancelled: true,
    };
  }

  const response = error?.response;
  const status = response?.status || error?.status || null;
  const data = response?.data ?? error?.data;
  const isOffline =
    typeof navigator !== "undefined" ? !navigator.onLine : false;

  // Handle Pydantic validation errors (FastAPI)
  let message;
  let parsedData = data;
  if (typeof parsedData === "string") {
    try {
      parsedData = JSON.parse(parsedData);
    } catch {
      // keep as plain string
    }
  }

  if (Array.isArray(parsedData?.detail)) {
    // Pydantic validation error format
    const errors = parsedData.detail
      .map((err) => err?.msg || JSON.stringify(err))
      .join(", ");
    message = errors || "Validation error";
  } else if (typeof parsedData?.detail === "string") {
    message = parsedData.detail;
  } else if (parsedData?.detail && typeof parsedData.detail === "object") {
    message =
      parsedData.detail.message ||
      parsedData.detail.error ||
      parsedData.detail.detail ||
      null;
  } else {
    const isHtmlString =
      typeof parsedData === "string" &&
      /^\s*</.test(parsedData) &&
      /<\/(html|body)>/i.test(parsedData);

    message =
      parsedData?.message ||
      parsedData?.error ||
      (!isHtmlString && typeof parsedData === "string" && parsedData.trim()
        ? parsedData.trim()
        : null) ||
      (isOffline
        ? "You appear to be offline. Please reconnect and retry."
        : error?.message || "Unexpected error");
  }

  return {
    message,
    status,
    data: parsedData,
    isOffline,
    isNetworkError: !status,
    originalError: error,
  };
};

const httpClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    "Content-Type": "application/json",
  },
});

let refreshPromise = null;

const refreshAuthToken = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = getStoredValue("vouchee_refresh_token");
  if (!refreshToken) {
    return null;
  }

  refreshPromise = axios
    .post(
      `${API_BASE_URL}/api/auth/token/refresh`,
      { refresh_token: refreshToken },
      {
        headers: { "Content-Type": "application/json" },
        timeout: REQUEST_TIMEOUT,
      },
    )
    .then((response) => {
      const { access_token: accessToken, refresh_token: nextRefreshToken } =
        response?.data || {};
      if (accessToken) {
        setStoredValue("vouchee_auth_token", accessToken);
      }
      if (nextRefreshToken) {
        setStoredValue("vouchee_refresh_token", nextRefreshToken);
      }
      return accessToken || null;
    })
    .catch(() => null)
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const shouldRetryRequest = (error, config) => {
  if (!config || config.__isRetryRequest) {
    return false;
  }

  const status = error?.status || error?.response?.status;
  if (status && !RETRYABLE_STATUS_CODES.has(status)) {
    return false;
  }

  if (!status && error?.code !== "ECONNABORTED") {
    return false;
  }

  config.__isRetryRequest = true;
  return true;
};

httpClient.interceptors.request.use(
  (config) => {
    removePendingRequest(config);
    const controller = new AbortController();
    config.signal = controller.signal;
    pendingRequests.set(getRequestKey(config), controller);

    const token = getStoredValue("vouchee_auth_token");
    if (token && !config.headers?.Authorization) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    config.headers = {
      "X-Client-Version": import.meta.env.VITE_APP_VERSION || "web-frontend",
      "X-Requested-With": "XMLHttpRequest",
      ...config.headers,
    };

    if (!config.headers["X-Request-Id"]) {
      const hasCrypto =
        typeof crypto !== "undefined" &&
        typeof crypto.randomUUID === "function";
      config.headers["X-Request-Id"] = hasCrypto
        ? crypto.randomUUID()
        : `req-${Date.now()}`;
    }

    return config;
  },
  (error) => Promise.reject(normalizeError(error)),
);

httpClient.interceptors.response.use(
  (response) => {
    removePendingRequest(response.config);
    return response.data ?? response;
  },
  async (error) => {
    const config = error?.config;
    removePendingRequest(config);
    const normalized = normalizeError(error);

    if (config && shouldRetryRequest(normalized, config)) {
      return httpClient(config);
    }

    if (normalized.status === 401 && !config?.__isTokenRefreshed) {
      const freshToken = await refreshAuthToken();

      if (freshToken) {
        config.__isTokenRefreshed = true;
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${freshToken}`,
        };
        return httpClient(config);
      }

      setStoredValue("vouchee_auth_token", null);
      setStoredValue("vouchee_refresh_token", null);
    }

    if (normalized.status === 401 || normalized.isCancelled) {
      return Promise.reject(normalized);
    }

    if (!config?.suppressErrorToast) {
      showErrorToast(error, {
        component: "API Interceptor",
        url: config?.url,
        method: config?.method,
      });
    }

    return Promise.reject(normalized);
  },
);

export const cancelPendingRequests = (reason = "Component unmounted") => {
  for (const controller of pendingRequests.values()) {
    controller.abort(reason);
  }
  pendingRequests.clear();
};

export default httpClient;
